import React, { useState } from 'react';
import { ChevronLeft, Filter, Users, Briefcase, Award, Star } from 'lucide-react';
import { FilterState } from '../utils/types';
interface FilterSidebarProps {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  toggleData: () => void;
  isRealData: boolean;
}
export const FilterSidebar = ({
  filters,
  setFilters,
  toggleData,
  isRealData
}: FilterSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggleArchetype = (archetype: keyof typeof filters.archetypes) => {
    setFilters(prev => ({
      ...prev,
      archetypes: {
        ...prev.archetypes,
        [archetype]: !prev.archetypes[archetype]
      }
    }));
  };
  const toggleSkill = (skill: string) => {
    setFilters(prev => ({
      ...prev,
      skills: {
        ...prev.skills,
        [skill]: !prev.skills[skill]
      }
    }));
  };
  const selectOnlySkill = (skill: string) => {
    setFilters(prev => {
      const updatedSkills = Object.keys(prev.skills).reduce((acc, currSkill) => {
        acc[currSkill] = currSkill === skill;
        return acc;
      }, {} as Record<string, boolean>);
      return {
        ...prev,
        skills: updatedSkills
      };
    });
  };
  const toggleAllSkills = () => {
    const areAllSelected = Object.values(filters.skills).every(value => value);
    setFilters(prev => {
      const updatedSkills = Object.keys(prev.skills).reduce((acc, skill) => {
        acc[skill] = !areAllSelected;
        return acc;
      }, {} as Record<string, boolean>);
      return {
        ...prev,
        skills: updatedSkills
      };
    });
  };
  const areAllSkillsSelected = Object.values(filters.skills).every(value => value);
  const togglePreviousExit = () => {
    setFilters(prev => ({
      ...prev,
      previousExit: !prev.previousExit
    }));
  };
  const toggleRepeatFounder = () => {
    setFilters(prev => ({
      ...prev,
      repeatFounder: !prev.repeatFounder
    }));
  };
  if (collapsed) {
    return <div className="w-12 bg-[#111] border-r border-[#222] flex flex-col items-center py-4" data-id="element-377">
        <button onClick={() => setCollapsed(false)} className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white mb-6" data-id="element-378">
          <ChevronLeft size={20} data-id="element-379" />
        </button>
        <button className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white mb-4" data-id="element-380">
          <Filter size={20} data-id="element-381" />
        </button>
        <button className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white mb-4" data-id="element-382">
          <Users size={20} data-id="element-383" />
        </button>
        <button className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white mb-4" data-id="element-384">
          <Briefcase size={20} data-id="element-385" />
        </button>
        <button className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-386">
          <Award size={20} data-id="element-387" />
        </button>
      </div>;
  }
  return <div className="w-64 bg-[#111] border-r border-[#222] flex flex-col h-full overflow-auto" data-id="element-388">
      <div className="flex justify-between items-center p-4 border-b border-[#222]" data-id="element-389">
        <h2 className="text-sm font-semibold" data-id="element-390">Filters</h2>
        <button onClick={() => setCollapsed(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-391">
          <ChevronLeft size={16} data-id="element-392" />
        </button>
      </div>
      <div className="p-4 border-b border-[#222]" data-id="element-393">
        <h3 className="text-xs font-semibold mb-3 text-gray-400 flex items-center" data-id="element-394">
          <Users size={14} className="mr-2" data-id="element-395" /> FOUNDER ARCHETYPE
        </h3>
        <div className="space-y-2" data-id="element-396">
          <div className="flex items-center" data-id="element-397">
            <input type="checkbox" id="ceo" checked={filters.archetypes.CEO} onChange={() => toggleArchetype('CEO')} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-purple-500 focus:ring-0 focus:ring-offset-0" data-id="element-398" />
            <label htmlFor="ceo" className="text-xs flex items-center" data-id="element-399">
              <span className="w-3 h-3 rounded-full bg-purple-500 mr-2" data-id="element-400"></span>
              CEO
            </label>
          </div>
          <div className="flex items-center" data-id="element-401">
            <input type="checkbox" id="cto" checked={filters.archetypes.CTO} onChange={() => toggleArchetype('CTO')} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-green-500 focus:ring-0 focus:ring-offset-0" data-id="element-402" />
            <label htmlFor="cto" className="text-xs flex items-center" data-id="element-403">
              <span className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-404"></span>
              CTO
            </label>
          </div>
        </div>
      </div>
      <div className="p-4 border-b border-[#222]" data-id="element-405">
        <h3 className="text-xs font-semibold mb-3 text-gray-400 flex items-center" data-id="element-406">
          <Briefcase size={14} className="mr-2" data-id="element-407" /> SKILL DOMAIN
        </h3>
        <div className="space-y-2" data-id="element-408">
          <div className="flex items-center" data-id="element-409">
            <input type="checkbox" id="selectAllSkills" checked={areAllSkillsSelected} onChange={toggleAllSkills} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-indigo-500 focus:ring-0 focus:ring-offset-0" data-id="element-410" />
            <label htmlFor="selectAllSkills" className="text-xs font-medium" data-id="element-411">
              Select All
            </label>
          </div>
          {Object.keys(filters.skills).map(skill => <div className="flex items-center" key={skill} data-id="element-412">
              <input type="checkbox" id={skill} checked={filters.skills[skill as keyof typeof filters.skills]} onChange={() => toggleSkill(skill)} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-indigo-500 focus:ring-0 focus:ring-offset-0" data-id="element-413" />
              <label htmlFor={skill} className="text-xs cursor-pointer" onClick={e => {
            e.preventDefault();
            selectOnlySkill(skill);
          }} data-id="element-414">
                {skill}
              </label>
            </div>)}
        </div>
      </div>
      <div className="p-4" data-id="element-415">
        <h3 className="text-xs font-semibold mb-3 text-gray-400 flex items-center" data-id="element-416">
          <Award size={14} className="mr-2" data-id="element-417" /> EXPERIENCE
        </h3>
        <div className="space-y-2" data-id="element-418">
          <div className="flex items-center" data-id="element-419">
            <input type="checkbox" id="previousExit" checked={filters.previousExit} onChange={togglePreviousExit} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-yellow-500 focus:ring-0 focus:ring-offset-0" data-id="element-420" />
            <label htmlFor="previousExit" className="text-xs" data-id="element-421">
              Has previous exit
            </label>
          </div>
          <div className="flex items-center" data-id="element-422">
            <input type="checkbox" id="repeatFounder" checked={filters.repeatFounder} onChange={toggleRepeatFounder} className="mr-2 h-4 w-4 rounded border-gray-700 bg-[#222] text-blue-500 focus:ring-0 focus:ring-offset-0" data-id="element-423" />
            <label htmlFor="repeatFounder" className="text-xs" data-id="element-424">
              Repeat founder
            </label>
          </div>
        </div>
      </div>
      <div className="mt-auto p-4" data-id="element-425">
        <button onClick={toggleData} className="text-xs text-[#111] hover:text-gray-300 transition-colors" data-id="element-426">
          Toggle
        </button>
      </div>
    </div>;
};